// ------------------------------- COMPONENT LIST TYPES ---------------------------------
export interface Component {
  shipmentId: string;
  productId: string;
  componentId: string;
  tcId: string;
  tcProductNo?: string; // product number (some old API payloads use this)
  tcAssociatedProductNo: string;
  tcProductComponentNo: string;
  tcAssociatedProductArticleNo: string;


  tcProductComponentOrder: string;
  tcProductComponentDetail: string;
  tcProductComponentNetShippingWeight: number;
  tcProductComponentSupplementaryWeight: number;
  tcProductComponentInfoFiberLength?: string;
  tcProductComponentInfoYarnCount?: string;
  tcProductComponentInfoFabricWeightAndConstruct?: string;
  tcProductComponentInfoFabricWeightOfFinishedProduct?: string;
  tcProductComponentInfoAdditional?: string;
}


// The API response is an array of Component objects.
export type ApiGetComponentList = Component[];

// ------------------------------- COMPONENT STANDARD INFO -------------------------------
export interface ComponentStandardInformation {
  tcProductComponentClaimsCategory: string;
  tcProductComponentClaimsCategoryDescription: string;
  tcProductComponentCertifiedWeight: number | null;
}

// ------------------------------- COMPONENT RAW MATERIAL INFO ---------------------------
export interface ComponentRawMaterialInformation {
  tcProductComponentRawMaterialPercentage: number | null;
  tcProductComponentRawMaterialCode: string;
  tcProductComponentRawMaterialCodeDescription: string;
}

// ------------------------------- COMPONENT INFORMATION -------------------------------
export interface TcComponentInformation {
  tcAssociatedProductArticleNo: string;
  tcProductComponentNo: string;
  tcProductComponentDetail: string;
  tcProductComponentInfoFiberLength: string;
  tcProductComponentInfoYarnCount: string;
  tcProductComponentInfoFabricWeightAndConstruct: string;
  tcProductComponentInfoFabricWeightOfFinishedProduct: string;
  tcProductComponentNetShippingWeight: number | null;
  tcProductComponentInfoAdditional: string;
  tcProductComponentInfoTechnicalSpecifications: string;
  tcProductComponentCertifiedWeightInformation: ComponentStandardInformation[];
  tcProductComponentRawMaterialInformation: ComponentRawMaterialInformation[];
}

// ------------------------------- MAIN ADDITIONAL DATA RESPONSE -------------------------
export interface ApiGetAdditionalData {
  tcId: string;
  productId: string;
  componentId: string;
  shipmentId: string;
  tcProductComponentInformation: TcComponentInformation[];
}

// ------------------------------- UI MAPPING MODELS -------------------------------------
export interface ComponentInfo {
  tcAssociatedProductNo: string;
  tcProductComponentOrder: string;
  tcProductComponentNo: string;
  tcProductComponentDetail: string;
  tcProductComponentInfoTrimsExcluded: string;
  tcProductComponentInfoAssociatedTc: string;
  tcProductComponentInfoFiberLength: string;
  tcProductComponentInfoYarnCount: string;
  tcProductComponentInfoFabricWeightAndConstruct: string;
  tcProductComponentInfoFabricWeightOfFinishedProduct: string;
  tcProductComponentNetShippingWeight: number | null;
  tcProductComponentSupplementaryWeight: number | null;
  tcProductComponentInfoAdditional: string;
  tcProductComponentInfoTechnicalSpecifications: string;
}

export interface StandardInfo {
  tcProductComponentClaimsCategory: string;
  tcProductComponentClaimsCategoryDescription: string;
  tcProductComponentCertifiedWeight: number | null;
}

export interface RawMaterialInfo {
  tcProductComponentRawMaterialPercentage: number | null;
  tcProductComponentRawMaterialCode: string;
  tcProductComponentRawMaterialCodeDescription: string;
}

// ------------------------------- MAPPED PAYLOAD (POST BODY) ----------------------------
export interface MappedComponentPayload {
  tcId: string;
  productId: string; // always required
  componentId?: string | null; // present for edit or reuse scenarios
  tcProductComponentInformation: Array<{
    tcAssociatedProductArticleNo: string;
    tcProductComponentNo: string;
    tcProductComponentDetail: string;
    tcProductComponentInfoFiberLength: string;
    tcProductComponentInfoYarnCount: string;
    tcProductComponentInfoFabricWeightAndConstruct: string;
    tcProductComponentInfoFabricWeightOfFinishedProduct: string;
    tcProductComponentNetShippingWeight: number | null;
    tcProductComponentInfoAdditional: string;
    tcProductComponentInfoTechnicalSpecifications: string;
    tcProductComponentCertifiedWeightInformation: Array<{
      tcProductComponentClaimsCategory: string;
      tcProductComponentClaimsCategoryDescription: string;
      tcProductComponentCertifiedWeight: number | null;
    }>;
    tcProductComponentRawMaterialInformation: Array<{
      tcProductComponentRawMaterialCode: string;
      tcProductComponentRawMaterialCodeDescription: string;
      tcProductComponentRawMaterialPercentage: number | null;
    }>;
  }>;
}

// ------------------------------- PRODUCT SHIPMENT RESPONSE -----------------------------
export interface ProductShipmentResponse {
  shipmentId: string;
  productId: string;
  tcId: string;
  tcProductShipmentDocNo: string[];
  tcProductNo: string;
  tcProductCategoryCode: string;
  tcNetShippingWeight: number;
  tcProductProductionDate: string; // Format: YYYY-MM-DD
  tcProductSupplementaryWeight: number;
  tcProductOrderNo: string[];
  tcProductArticleNo: string;
  tcProductNumberOfUnits: number;
  tcProductUnitsMetric: string;
  tcProductInfoAssociatedTc: string;
}

/**
 * Finds a shipment by its tcProductNo and returns productId + shipmentId.
 */
export const getProductShipmentDataByProductNo = (
  data: any,
  tcProductNo: string
): { productId: string; shipmentId: string[]; tcProductShipmentDocNo: string[] } | undefined => {
  const normalizedData = Array.isArray(data)
    ? data
    : Array.isArray(data?.response)
    ? data.response
    : [];

  if (!normalizedData.length) {
    console.error(" No valid shipment data found:", data);
    return undefined;
  }

  const shipments = normalizedData.filter(
    (shipment: any) => shipment.tcProductArticleNo === tcProductNo
  );

  if (!shipments.length) {
    console.warn(" No shipment found for article number:", tcProductNo);
    return undefined;
  }

  return {
    productId: shipments[0].productId,
    shipmentId: shipments.map((s: any) => s.shipmentId),
    tcProductShipmentDocNo: shipments.flatMap((s: any) => s.tcProductShipmentDocNo || []),
  };
};

/**
 * Maps an ApiGetAdditionalData object to a frontend ApiComponentData model.
 */
export const mapToApiComponentData = (
  data: Partial<ApiGetAdditionalData> | TcComponentInformation
): any => {
  const sourceComponent: TcComponentInformation =
    "tcProductComponentInformation" in data &&
    Array.isArray(data.tcProductComponentInformation) &&
    data.tcProductComponentInformation.length > 0
      ? data.tcProductComponentInformation[0]
      : (data as TcComponentInformation);

  const componentInfo: ComponentInfo = {
    tcAssociatedProductNo: sourceComponent.tcAssociatedProductArticleNo,
    tcProductComponentOrder: "",
    tcProductComponentNo: sourceComponent.tcProductComponentNo,
    tcProductComponentDetail: sourceComponent.tcProductComponentDetail,
    tcProductComponentInfoTrimsExcluded: "",
    tcProductComponentInfoAssociatedTc: "",
    tcProductComponentInfoFiberLength: sourceComponent.tcProductComponentInfoFiberLength,
    tcProductComponentInfoYarnCount: sourceComponent.tcProductComponentInfoYarnCount,
    tcProductComponentInfoFabricWeightAndConstruct:
      sourceComponent.tcProductComponentInfoFabricWeightAndConstruct,
    tcProductComponentInfoFabricWeightOfFinishedProduct:
      sourceComponent.tcProductComponentInfoFabricWeightOfFinishedProduct,
    tcProductComponentNetShippingWeight: sourceComponent.tcProductComponentNetShippingWeight,
    tcProductComponentSupplementaryWeight: null,
    tcProductComponentInfoAdditional: sourceComponent.tcProductComponentInfoAdditional,
    tcProductComponentInfoTechnicalSpecifications:
      sourceComponent.tcProductComponentInfoTechnicalSpecifications,
  };

  const standardInfo: StandardInfo[] = (sourceComponent.tcProductComponentCertifiedWeightInformation || []).map(
    (item) => ({
      tcProductComponentClaimsCategory: item.tcProductComponentClaimsCategory,
      tcProductComponentClaimsCategoryDescription: item.tcProductComponentClaimsCategoryDescription,
      tcProductComponentCertifiedWeight: item.tcProductComponentCertifiedWeight ?? null,
    })
  );

  const rawMaterialInfo: RawMaterialInfo[] = (sourceComponent.tcProductComponentRawMaterialInformation || []).map(
    (item) => ({
      tcProductComponentRawMaterialPercentage: item.tcProductComponentRawMaterialPercentage ?? null,
      tcProductComponentRawMaterialCode: item.tcProductComponentRawMaterialCode,
      tcProductComponentRawMaterialCodeDescription: item.tcProductComponentRawMaterialCodeDescription,
    })
  );

  return {
    componentInfo,
    standardInfo,
    rawMaterialInfo,
  };
};
